import type { DiscordActivityContext } from './discordSdk'

export type ReadyState = 'pronto' | 'aguardando' | 'ausente'
export type MapState = 'open' | 'ban' | 'pick'
export type Team = 'A' | 'B'

export interface ActivityPlayer {
  displayName: string
  role: string
  rating: string
  ready: ReadyState
  team: Team
  joinedAt?: number
}

export interface MapPoolItem {
  key: string
  name: string
  state: MapState
}

export interface MatchroomSnapshot {
  version: 1
  generatedAt: number
  source: 'bot' | 'sample'
  room: {
    id: string
    mode: '5v5' | '2v2' | 'retake' | 'review'
    label: string
    status: 'aberta' | 'cancelada' | 'finalizada'
    capacity: number
    playerCount: number
    readyCount: number
    balance: {
      teamAScore: number
      teamBScore: number
      source: 'linked-ratings'
    }
    createdAt: number
    expiresAt: number
    activity: {
      enabled: boolean
      launchAttempted: boolean
      launchedAt: number | null
      lastLaunchError: string | null
    }
  }
  teams: Record<Team, ActivityPlayer[]>
  maps: MapPoolItem[]
  voiceRooms: Array<{
    team: Team
    label: string
    status: 'standby' | 'ready' | 'unavailable'
  }>
  recap: {
    demoOptIn: boolean
    clipsOptIn: boolean
    mvpCandidates: Array<{
      candidateKey: string
      displayName: string
      votes: number
    }>
    result: {
      teamARounds: number
      teamBRounds: number
      winner: Team
      mvpDisplayName: string | null
      highlight: string | null
    } | null
  }
  logLines: string[]
}

export interface LoadedMatchroomSnapshot {
  snapshot: MatchroomSnapshot
  warning?: string
}

const apiBase = import.meta.env.VITE_MATCHROOM_API_BASE as string | undefined

export function createPreviewSnapshot(): MatchroomSnapshot {
  const now = Math.floor(Date.now() / 1000)
  const players: ActivityPlayer[] = [
    { displayName: 'Costa', role: 'IGL', rating: 'Faceit 8', ready: 'pronto', team: 'A' },
    { displayName: 'Vini', role: 'Entry', rating: 'Premier 16k', ready: 'pronto', team: 'A' },
    { displayName: 'Rafa', role: 'AWP', rating: 'GC 18', ready: 'aguardando', team: 'A' },
    { displayName: 'Lima', role: 'Support', rating: 'Faceit 6', ready: 'ausente', team: 'A' },
    { displayName: 'Dani', role: 'Lurk', rating: 'Premier 14k', ready: 'aguardando', team: 'A' },
    { displayName: 'Moura', role: 'IGL', rating: 'Faceit 9', ready: 'pronto', team: 'B' },
    { displayName: 'Akira', role: 'Entry', rating: 'GC 20', ready: 'pronto', team: 'B' },
    { displayName: 'Neto', role: 'AWP', rating: 'Premier 17k', ready: 'aguardando', team: 'B' },
    { displayName: 'Bia', role: 'Support', rating: 'Faceit 7', ready: 'pronto', team: 'B' },
    { displayName: 'Theo', role: 'Lurk', rating: 'Premier 13k', ready: 'aguardando', team: 'B' }
  ]

  return {
    version: 1,
    generatedAt: now,
    source: 'sample',
    room: {
      id: 'A7F2',
      mode: '5v5',
      label: '5v5 competitivo',
      status: 'aberta',
      capacity: 10,
      playerCount: players.length,
      readyCount: players.filter((player) => player.ready === 'pronto').length,
      balance: {
        teamAScore: 8400,
        teamBScore: 8200,
        source: 'linked-ratings'
      },
      createdAt: now - 420,
      expiresAt: now + 4200,
      activity: {
        enabled: false,
        launchAttempted: false,
        launchedAt: null,
        lastLaunchError: null
      }
    },
    teams: {
      A: players.filter((player) => player.team === 'A'),
      B: players.filter((player) => player.team === 'B')
    },
    maps: [
      { key: 'mirage', name: 'Mirage', state: 'open' },
      { key: 'inferno', name: 'Inferno', state: 'ban' },
      { key: 'nuke', name: 'Nuke', state: 'open' },
      { key: 'ancient', name: 'Ancient', state: 'pick' },
      { key: 'anubis', name: 'Anubis', state: 'open' },
      { key: 'vertigo', name: 'Vertigo', state: 'ban' },
      { key: 'dust2', name: 'Dust2', state: 'open' }
    ],
    voiceRooms: [
      { team: 'A', label: 'Time A', status: 'standby' },
      { team: 'B', label: 'Time B', status: 'standby' }
    ],
    recap: {
      demoOptIn: true,
      clipsOptIn: true,
      mvpCandidates: [
        { candidateKey: 'p0', displayName: 'Costa', votes: 42 },
        { candidateKey: 'p2', displayName: 'Rafa', votes: 28 },
        { candidateKey: 'p6', displayName: 'Akira', votes: 20 },
        { candidateKey: 'p8', displayName: 'Bia', votes: 10 }
      ],
      result: null
    },
    logLines: [
      'Costa marcou pronto',
      'Inferno banido pelo Team B',
      'Ancient escolhido como mapa provavel',
      'Sala Time A pronta para mover jogadores'
    ]
  }
}

export async function loadMatchroomSnapshot(
  context: DiscordActivityContext
): Promise<LoadedMatchroomSnapshot> {
  const local = createPreviewSnapshot()
  const url = buildSnapshotUrl(apiBase, context)
  if (!url) return { snapshot: local }

  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 2500)

  try {
    const response = await fetch(url, {
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'omit',
      headers: {
        accept: 'application/json',
        ...(context.accessToken ? { authorization: `Bearer ${context.accessToken}` } : {})
      }
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const snapshot = normalizeSnapshot(await response.json())
    if (!snapshot) throw new Error('invalid snapshot')
    return {
      snapshot,
      warning:
        snapshot.source === 'sample'
          ? 'Snapshot do Worker em preview; runtime do bot ainda nao conectado.'
          : undefined
    }
  } catch {
    return {
      snapshot: local,
      warning: 'Snapshot remoto indisponivel; usando preview local.'
    }
  } finally {
    window.clearTimeout(timeout)
  }
}

export async function mutateMatchroom(
  context: DiscordActivityContext,
  action: 'ready' | 'demo' | 'clips' | 'map' | 'mvp' | 'voice',
  body: Record<string, unknown> = {}
): Promise<LoadedMatchroomSnapshot> {
  const local = createPreviewSnapshot()
  const url = buildActionUrl(apiBase, context, action)
  if (!url || !context.accessToken) {
    return { snapshot: local, warning: 'Runtime autenticado indisponivel.' }
  }

  const controller = new AbortController()
  const timeout = window.setTimeout(() => controller.abort(), 2500)

  try {
    const response = await fetch(url, {
      method: 'POST',
      signal: controller.signal,
      cache: 'no-store',
      credentials: 'omit',
      headers: {
        accept: 'application/json',
        'content-type': 'application/json',
        authorization: `Bearer ${context.accessToken}`
      },
      body: JSON.stringify(body)
    })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const payload = record(await response.json())
    const snapshot = normalizeSnapshot(payload?.snapshot)
    if (!snapshot) throw new Error('invalid mutation snapshot')
    return { snapshot }
  } catch {
    return {
      snapshot: local,
      warning: 'Acao remota falhou; estado local voltou para preview.'
    }
  } finally {
    window.clearTimeout(timeout)
  }
}

function buildSnapshotUrl(
  base: string | undefined,
  context: DiscordActivityContext
): URL | null {
  return buildApiUrl(base, context, 'matchroom/snapshot')
}

function buildActionUrl(
  base: string | undefined,
  context: DiscordActivityContext,
  action: string
): URL | null {
  return buildApiUrl(base, context, `matchroom/${action}`)
}

function buildApiUrl(
  base: string | undefined,
  context: DiscordActivityContext,
  path: string
): URL | null {
  const trimmed = base?.trim()
  const withContext = (url: URL): URL => {
    if (context.guildId) url.searchParams.set('guildId', context.guildId)
    if (context.channelId) url.searchParams.set('channelId', context.channelId)
    return url
  }
  if (!trimmed) {
    return import.meta.env.PROD
      ? withContext(new URL(`/api/${path}`, window.location.origin))
      : null
  }

  try {
    if (trimmed.startsWith('/')) {
      const href = trimmed.endsWith('/') ? trimmed : `${trimmed}/`
      return withContext(new URL(path, new URL(href, window.location.origin)))
    }

    const baseUrl = new URL(trimmed)
    const localHost =
      baseUrl.hostname === 'localhost' ||
      baseUrl.hostname === '127.0.0.1' ||
      baseUrl.hostname === '[::1]'
    if (baseUrl.protocol !== 'https:' && !localHost) return null
    const href = trimmed.endsWith('/') ? trimmed : `${trimmed}/`
    return withContext(new URL(path, href))
  } catch {
    return null
  }
}

function normalizeSnapshot(input: unknown): MatchroomSnapshot | null {
  const root = record(input)
  const room = record(root?.room)
  const teams = record(root?.teams)
  const recap = record(root?.recap)
  if (!root || !room || !teams || !recap || root.version !== 1) return null

  const playersA = normalizePlayers(teams.A, 'A')
  const playersB = normalizePlayers(teams.B, 'B')
  const players = [...playersA, ...playersB]
  const maps = normalizeMaps(root.maps)
  const mvpCandidates = normalizeMvpCandidates(recap.mvpCandidates, players)

  return {
    version: 1,
    generatedAt: numberValue(root.generatedAt, Math.floor(Date.now() / 1000)),
    source: snapshotSource(root.source),
    room: {
      id: cleanText(room.id, 'local', 16),
      mode: modeValue(room.mode),
      label: cleanText(room.label, 'Matchroom', 48),
      status: statusValue(room.status),
      capacity: clampNumber(room.capacity, 1, 10, 10),
      playerCount: clampNumber(room.playerCount, 0, 10, players.length),
      readyCount: clampNumber(room.readyCount, 0, 10, 0),
      balance: normalizeBalance(room.balance),
      createdAt: numberValue(room.createdAt, 0),
      expiresAt: numberValue(room.expiresAt, 0),
      activity: {
        enabled: Boolean(record(room.activity)?.enabled),
        launchAttempted: Boolean(record(room.activity)?.launchAttempted),
        launchedAt: nullableNumber(record(room.activity)?.launchedAt),
        lastLaunchError: nullableText(record(room.activity)?.lastLaunchError, 120)
      }
    },
    teams: { A: playersA, B: playersB },
    maps,
    voiceRooms: normalizeVoiceRooms(root.voiceRooms),
    recap: {
      demoOptIn: Boolean(recap.demoOptIn),
      clipsOptIn: Boolean(recap.clipsOptIn),
      mvpCandidates,
      result: normalizeRecapResult(recap.result)
    },
    logLines: normalizeLogLines(root.logLines)
  }
}

function normalizeRecapResult(
  input: unknown
): MatchroomSnapshot['recap']['result'] {
  const result = record(input)
  if (!result) return null
  return {
    teamARounds: clampNumber(result.teamARounds, 0, 99, 0),
    teamBRounds: clampNumber(result.teamBRounds, 0, 99, 0),
    winner: result.winner === 'B' ? 'B' : 'A',
    mvpDisplayName: nullableText(result.mvpDisplayName, 40),
    highlight: nullableText(result.highlight, 120)
  }
}

function normalizePlayers(input: unknown, team: Team): ActivityPlayer[] {
  if (!Array.isArray(input)) return []
  return input.slice(0, 5).map((item, index) => {
    const player = record(item)
    return {
      displayName: cleanText(player?.displayName, `Jogador ${index + 1}`, 40),
      role: cleanText(player?.role, 'Jogador', 24),
      rating: cleanText(player?.rating, 'A definir', 28),
      ready: readyValue(player?.ready),
      team,
      joinedAt: nullableNumber(player?.joinedAt) ?? undefined
    }
  })
}

function normalizeMaps(input: unknown): MapPoolItem[] {
  if (!Array.isArray(input)) return createPreviewSnapshot().maps
  return input.slice(0, 9).map((item, index) => {
    const map = record(item)
    return {
      key: cleanText(map?.key, `map-${index + 1}`, 24),
      name: cleanText(map?.name, `Mapa ${index + 1}`, 32),
      state: mapStateValue(map?.state)
    }
  })
}

function normalizeVoiceRooms(input: unknown): MatchroomSnapshot['voiceRooms'] {
  if (!Array.isArray(input)) return createPreviewSnapshot().voiceRooms
  return input.slice(0, 2).map((item, index) => {
    const room = record(item)
    const team = room?.team === 'B' ? 'B' : 'A'
    const status = room?.status === 'ready' || room?.status === 'unavailable'
      ? room.status
      : 'standby'
    return {
      team,
      label: cleanText(room?.label, index === 0 ? 'Time A' : 'Time B', 32),
      status
    }
  })
}

function normalizeMvpCandidates(
  input: unknown,
  players: ActivityPlayer[]
): MatchroomSnapshot['recap']['mvpCandidates'] {
  if (Array.isArray(input) && input.length > 0) {
    return input.slice(0, 6).map((item, index) => {
      const candidate = record(item)
      return {
        candidateKey: cleanText(candidate?.candidateKey, `p${index}`, 12),
        displayName: cleanText(candidate?.displayName, `Jogador ${index + 1}`, 40),
        votes: clampNumber(candidate?.votes, 0, 100, 0)
      }
    })
  }
  return players.slice(0, 6).map((player) => ({
    candidateKey: `p${players.indexOf(player)}`,
    displayName: player.displayName,
    votes: 0
  }))
}

function normalizeBalance(input: unknown): MatchroomSnapshot['room']['balance'] {
  const balance = record(input)
  return {
    teamAScore: clampNumber(balance?.teamAScore, 0, 500000, 0),
    teamBScore: clampNumber(balance?.teamBScore, 0, 500000, 0),
    source: 'linked-ratings'
  }
}

function normalizeLogLines(input: unknown): string[] {
  if (!Array.isArray(input)) return createPreviewSnapshot().logLines
  return input.slice(0, 6).map((item, index) =>
    cleanText(item, `Evento ${index + 1}`, 80)
  )
}

function record(input: unknown): Record<string, unknown> | null {
  return input && typeof input === 'object' && !Array.isArray(input)
    ? (input as Record<string, unknown>)
    : null
}

function cleanText(input: unknown, fallback: string, maxLength: number): string {
  if (typeof input !== 'string') return fallback
  const cleaned = input
    .replace(/@everyone|@here/gi, '[mention removida]')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, maxLength)
  return cleaned || fallback
}

function nullableText(input: unknown, maxLength: number): string | null {
  if (input === null || input === undefined) return null
  return cleanText(input, '', maxLength) || null
}

function numberValue(input: unknown, fallback: number): number {
  return typeof input === 'number' && Number.isFinite(input) ? Math.floor(input) : fallback
}

function nullableNumber(input: unknown): number | null {
  return typeof input === 'number' && Number.isFinite(input) ? Math.floor(input) : null
}

function clampNumber(
  input: unknown,
  min: number,
  max: number,
  fallback: number
): number {
  const value = numberValue(input, fallback)
  return Math.max(min, Math.min(max, value))
}

function modeValue(input: unknown): MatchroomSnapshot['room']['mode'] {
  return input === '2v2' || input === 'retake' || input === 'review'
    ? input
    : '5v5'
}

function statusValue(input: unknown): MatchroomSnapshot['room']['status'] {
  if (input === 'cancelada' || input === 'finalizada') return input
  return 'aberta'
}

function readyValue(input: unknown): ReadyState {
  if (input === 'pronto' || input === 'ausente') return input
  return 'aguardando'
}

function mapStateValue(input: unknown): MapState {
  if (input === 'ban' || input === 'pick') return input
  return 'open'
}

function snapshotSource(input: unknown): MatchroomSnapshot['source'] {
  return input === 'bot' ? 'bot' : 'sample'
}
